// Doctor/clinic change & cancellation requests on a case: list, file a new one, and (for reviewers)
// approve/reject pending ones. Refreshes off the request SSEs.
import { useCallback, useEffect, useState } from 'react';
import {
  type CaseRequest,
  type CaseRequestKind,
  CASE_REQUEST_KINDS,
  CASE_REQUEST_KIND_LABELS,
  CASE_REQUEST_STATUS_LABELS,
  formatDateTime,
} from '@alignova/shared';
import { api } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button, Pill } from '../../ui/primitives.tsx';

const inputClass = 'w-full rounded border border-line bg-surface px-3 py-2 text-sm';

export function CaseRequests({ caseId }: { caseId: string }) {
  const can = useCan();
  const canReview = can('case:approve_setup');
  const [items, setItems] = useState<CaseRequest[]>([]);
  const [kind, setKind] = useState<CaseRequestKind>('change');
  const [requestType, setRequestType] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get<CaseRequest[]>(`/cases/${caseId}/requests`).then(setItems).catch(() => setItems([]));
  }, [caseId]);

  useEffect(() => load(), [load]);
  useLiveEvent('case.request.created', () => load());
  useLiveEvent('case.request.reviewed', () => load());

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!description.trim()) return;
    setError(null);
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/requests`, {
        kind,
        requestType: requestType.trim() || undefined,
        description: description.trim(),
      });
      setRequestType('');
      setDescription('');
      load();
    } catch {
      setError(t('cases.requests.error'));
    } finally {
      setBusy(false);
    }
  }

  async function review(reqId: string, status: 'approved' | 'rejected') {
    setError(null);
    try {
      await api.post(`/cases/requests/${reqId}/review`, { status, reviewNote: null });
      load();
    } catch {
      setError(t('cases.requests.reviewError'));
    }
  }

  return (
    <div>
      {items.length === 0 ? (
        <p className="mb-4 text-sm text-muted">{t('cases.requests.empty')}</p>
      ) : (
        <ul className="mb-4 space-y-3">
          {items.map((r) => (
            <li key={r.id} className="rounded border border-line bg-surface-2 px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone={r.kind === 'cancel' ? 'bad' : 'neutral'}>
                  {CASE_REQUEST_KIND_LABELS[r.kind]}
                </Pill>
                {r.requestType && <span className="text-xs text-muted">{r.requestType}</span>}
                <Pill tone={r.status === 'approved' ? 'good' : r.status === 'rejected' ? 'bad' : 'neutral'}>
                  {CASE_REQUEST_STATUS_LABELS[r.status]}
                </Pill>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{r.description}</p>
              <p className="mt-1 text-xs text-muted">
                {r.requestedByName ?? '-'} · {formatDateTime(r.createdAt)}
              </p>
              {r.reviewNote && <p className="mt-1 text-xs text-muted">“{r.reviewNote}”</p>}
              {canReview && r.status === 'pending' && (
                <div className="mt-2 flex gap-2">
                  <Button className="px-3 py-1 text-xs" onClick={() => review(r.id, 'approved')}>
                    {t('cases.requests.approve')}
                  </Button>
                  <Button
                    variant="ghost"
                    className="px-3 py-1 text-xs text-status-urgent-fg"
                    onClick={() => review(r.id, 'rejected')}
                  >
                    {t('cases.requests.reject')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {can('case:update') && (
        <form className="space-y-3 border-t border-line pt-4" onSubmit={submit}>
          <h3 className="text-sm font-semibold text-ink">{t('cases.requests.new')}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.requests.kind')}</label>
              <select
                className={inputClass}
                value={kind}
                onChange={(e) => setKind(e.target.value as CaseRequestKind)}
              >
                {CASE_REQUEST_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {CASE_REQUEST_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.requests.requestType')}</label>
              <input className={inputClass} value={requestType} onChange={(e) => setRequestType(e.target.value)} />
            </div>
          </div>
          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.requests.description')}</label>
            <textarea
              className={inputClass}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {error && <p className="text-xs text-status-urgent-fg">{error}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={busy || !description.trim()}>
              {t('cases.requests.submit')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
